import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";

type DeviceInventory = components["schemas"]["DeviceInventory"];
type DeviceInventoryListResponse = components["schemas"]["DeviceInventoryListResponse"];
type DeviceInventoryCreate = components["schemas"]["DeviceInventoryCreate"];
type DeviceInventoryUpdate = components["schemas"]["DeviceInventoryUpdate"];
type DeviceInventoryRetireRequest = components["schemas"]["DeviceInventoryRetireRequest"];
type InstallationChainListResponse = components["schemas"]["InstallationChainListResponse"];

// Inventory rows change less frequently than installations (the physical
// device's identity tuple is IUR per AWWA M14 §6); a 10-minute staleTime
// keeps list pagination snappy while mutation invalidations cover edits.
const LIST_STALE_TIME = 10 * 60 * 1000;
const LIST_GC_TIME = 60 * 60 * 1000;
const DETAIL_STALE_TIME = 5 * 60 * 1000;
const DETAIL_GC_TIME = 30 * 60 * 1000;
const CHAIN_STALE_TIME = 5 * 60 * 1000;
const CHAIN_GC_TIME = 30 * 60 * 1000;

export interface DeviceInventoryListParams {
  manufacturer_id?: string;
  retired_only?: boolean;
  limit?: number;
  offset?: number;
  sort_by?:
    | "serial_number"
    | "first_seen_at"
    | "retired_at"
    | "created_at"
    | "updated_at"
    | "verdict";
  sort_dir?: "asc" | "desc";
}

export const deviceInventoryKeys = {
  all: ["deviceInventory"] as const,
  lists: () => [...deviceInventoryKeys.all, "list"] as const,
  list: (params: DeviceInventoryListParams) => [...deviceInventoryKeys.lists(), params] as const,
  details: () => [...deviceInventoryKeys.all, "detail"] as const,
  detail: (id: string) => [...deviceInventoryKeys.details(), id] as const,
} as const;

export const installationChainKeys = {
  all: ["installationChain"] as const,
  detail: (inventoryId: string) => [...installationChainKeys.all, "detail", inventoryId] as const,
} as const;

function withDefaults(params: DeviceInventoryListParams): DeviceInventoryListParams {
  return {
    ...params,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
}

/**
 * List device inventory rows with optional manufacturer / retired filters. Per
 * AWWA M14 §6 the inventory row is the physical device-of-record — its identity
 * tuple persists across installations. Ownership is per-installation now (D4),
 * so there is no owner filter on this register.
 */
export function useDeviceInventory(params: DeviceInventoryListParams = {}) {
  const merged = withDefaults(params);
  return useQuery<DeviceInventoryListResponse>({
    queryKey: deviceInventoryKeys.list(merged),
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET("/device-inventory", {
        params: { query: merged },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list device inventory";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useDeviceInventoryDetail(id: string | null | undefined) {
  return useQuery<DeviceInventory>({
    queryKey: deviceInventoryKeys.detail(id ?? ""),
    queryFn: async () => {
      if (!id) throw new Error("Device inventory id is required");
      const { data, error, response } = await apiClient.GET("/device-inventory/{id}", {
        params: { path: { id } },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load device inventory";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    enabled: Boolean(id),
    staleTime: DETAIL_STALE_TIME,
    gcTime: DETAIL_GC_TIME,
  });
}

/**
 * Chronological installation chain for a single inventory row. Returned by
 * GET /device-inventory/{id}/installations. The chain is forensic evidence
 * per AWWA M14 §6.5 lifecycle visibility.
 */
export function useInstallationChain(inventoryId: string | null | undefined) {
  return useQuery<InstallationChainListResponse>({
    queryKey: installationChainKeys.detail(inventoryId ?? ""),
    queryFn: async () => {
      if (!inventoryId) throw new Error("Inventory id is required");
      const { data, error, response } = await apiClient.GET(
        "/device-inventory/{id}/installations",
        { params: { path: { id: inventoryId } } },
      );
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load installation chain";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    enabled: Boolean(inventoryId),
    staleTime: CHAIN_STALE_TIME,
    gcTime: CHAIN_GC_TIME,
  });
}

export function useCreateDeviceInventory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: DeviceInventoryCreate) => {
      const { data, error, response } = await apiClient.POST("/device-inventory", {
        body,
      });
      if (error) {
        // The soft-409 envelope `{ error: "possible_duplicates", candidates: [...] }`
        // is threaded through ApiError.details so the soft-duplicate dialog
        // can render the candidate list and re-POST with force_create: true.
        const typed = error as { error?: string; candidates?: unknown };
        const msg = typed.error ?? "Failed to create device inventory";
        throw new ApiError(response.status, msg, undefined, typed);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceInventoryKeys.lists() });
    },
  });
}

export function useUpdateDeviceInventory(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: DeviceInventoryUpdate) => {
      const { data, error, response } = await apiClient.PATCH("/device-inventory/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) {
        const typed = error as { error?: string };
        const msg = typed.error ?? "Failed to update device inventory";
        throw new ApiError(response.status, msg, undefined, typed);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceInventoryKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: deviceInventoryKeys.lists() });
    },
  });
}

export function useRetireDeviceInventory(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: DeviceInventoryRetireRequest) => {
      const { data, error, response } = await apiClient.POST("/device-inventory/{id}/retire", {
        params: { path: { id } },
        body,
      });
      if (error) {
        const typed = error as { error?: string };
        const msg = typed.error ?? "Failed to retire device inventory";
        throw new ApiError(response.status, msg, undefined, typed);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceInventoryKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: deviceInventoryKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: installationChainKeys.detail(id),
      });
    },
  });
}
